import React, { useEffect, useState } from 'react';
import DataTable from '../../components/tables/DataTable';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import StatCard from '../../components/charts/StatCard';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import Toast from '../../components/common/Toast';
import { useToast } from '../../hooks/useToast';
import teamLeadService from '../../services/teamLeadService';

import AddTeamLeadModal from '../../components/features/teamLeads/AddTeamLeadModal';
import EditTeamLeadModal from '../../components/features/teamLeads/EditTeamLeadModal';

const TeamLeadsView = () => {
  const [teamLeads, setTeamLeads] = useState([]);
  const [filteredTeamLeads, setFilteredTeamLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTeamLead, setSelectedTeamLead] = useState(null);

  // Toast notifications
  const { toasts, removeToast, success, error } = useToast();

  useEffect(() => {
    loadTeamLeads();
  }, []);

  useEffect(() => {
    filterTeamLeads();
  }, [searchTerm, statusFilter, teamLeads]);

  const loadTeamLeads = async () => {
    setLoading(true);
    const result = await teamLeadService.getAllTeamLeads();
    if (result.success) {
      setTeamLeads(result.data);
      setFilteredTeamLeads(result.data);
    } else {
      error(result.error || 'Failed to load Team Leads');
    }
    setLoading(false);
  };

  const filterTeamLeads = () => {
    let filtered = [...teamLeads];

    if (searchTerm) {
      filtered = filtered.filter(tl =>
        tl.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tl.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tl.supervisor?.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(tl => tl.status === statusFilter);
    }

    setFilteredTeamLeads(filtered);
  };

  const handleAddTeamLead = async (tlData) => {
    const result = await teamLeadService.createTeamLead(tlData);
    if (result.success) {
      success('Team Lead added successfully!');
      await loadTeamLeads();
    } else {
      error(result.error || 'Failed to add Team Lead');
    }
  };

  const handleEditTeamLead = (tl) => {
    setSelectedTeamLead(tl);
    setShowEditModal(true);
  };

  const handleUpdateTeamLead = async (tlId, tlData) => {
    const result = await teamLeadService.updateTeamLead(tlId, tlData);
    if (result.success) {
      success('Team Lead updated successfully!');
      await loadTeamLeads();
    } else {
      error(result.error || 'Failed to update Team Lead');
    }
  };

  const handleDeleteTeamLead = (tl) => {
    setSelectedTeamLead(tl);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    const result = await teamLeadService.deleteTeamLead(selectedTeamLead.id);
    if (result.success) {
      success('Team Lead deleted successfully!');
      await loadTeamLeads();
    } else {
      error(result.error || 'Failed to delete Team Lead');
    }
    setShowDeleteDialog(false);
    setSelectedTeamLead(null);
  };

  const handleViewPerformance = async (tl) => {
    const result = await teamLeadService.getTeamLeadPerformance(tl.id);
    if (result.success) {
      console.log('Performance data:', result.data);
      success('Performance data loaded!');
    } else {
      error(result.error || 'Failed to load performance data');
    }
  };

  const getTeamLeadStats = () => {
    const total = teamLeads.length;
    const active = teamLeads.filter(tl => tl.is_active === true).length;
    const available = teamLeads.filter(tl => tl.status === 'Available').length;
    
    return { total, active, available };
  };

  const stats = getTeamLeadStats();

  const filterBarStyle = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    alignItems: 'flex-end'
  };

  const filterGroupStyle = {
    flex: '1',
    minWidth: '200px'
  };

  const selectStyle = {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    background: 'var(--surface)',
    color: 'var(--text-primary)',
    outline: 'none',
    fontFamily: 'inherit'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '32px'
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return '#10b981';
      case 'Available': return '#3b82f6';
      case 'Busy': return '#f59e0b';
      case 'On Leave': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Team Lead',
      render: (value, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: 'var(--primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: '700',
            fontSize: '16px'
          }}>
            {value.charAt(0)}
          </div>
          <div>
            <div style={{ fontWeight: '600' }}>{value}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {row.email}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'supervisor',
      label: 'Assigned Manager',
      render: (value) => value?.name || <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Unassigned</span>
    },
    {
      key: 'contact',
      label: 'Contact'
    },
    {
      key: 'location',
      label: 'Location / Site',
      render: (value, row) => {
        console.log(row)
        const firstSite = (Array.isArray(row.sites) && row.sites.length > 0)
          ? row.sites[0]
          : row.site;
        const locationDisplay = firstSite?.location || 'No Location';
        const siteNames = Array.isArray(row.sites) && row.sites.length > 0
          ? row.sites.map((site) => site.name || site.site_name).filter(Boolean)
          : row.site?.name || row.site_name
            ? [row.site?.name || row.site_name]
            : [];
        // const siteDisplay = siteNames.length > 0 ? siteNames.join(', ') : 'No Site';
        const siteDisplay = siteNames.length > 0 ? siteNames.length===1 ? siteNames[0] : siteNames.length +' Sites' : 'No Sites Assigned';
        return (
          <div>
            <div style={{ fontWeight: '500', color: 'var(--text-primary)' }}>{locationDisplay}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }} title={siteDisplay}>{siteDisplay}</div>
          </div>
        );
      }
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          background: `${getStatusColor(value)}20`,
          color: getStatusColor(value)
        }}>
          {value}
        </span>
      )
    },
    {
      key: 'engineers_count',
      label: 'Engineers',
      render: (value) => (
        <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
          {value || 0}
        </span>
      )
    },
    {
      key: 'active_tickets',
      label: 'Active Tickets',
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '700',
          background: 'var(--surface-light)',
          color: 'var(--text-primary)'
        }}>
          {value || 0}
        </span>
      )
    },
    {
      key: 'completed_this_month',
      label: 'Completed',
      render: (value) => `${value || 0} tickets`
    }
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>
        Loading team leads...
      </div>
    );
  }

  // Generate delete confirmation message
  let deleteMessage = `Are you sure you want to deactivate Team Lead ${selectedTeamLead?.name}? They will no longer be able to log in or manage their team.`;
  if (selectedTeamLead?.engineers_count > 0 || selectedTeamLead?.active_tickets > 0) {
    deleteMessage = `Warning: Team Lead ${selectedTeamLead?.name} has ${selectedTeamLead?.engineers_count || 0} engineer(s) assigned and ${selectedTeamLead?.active_tickets || 0} active team ticket(s). Deactivating them may affect team operations. Are you sure?`;
  }

  return (
    <div>
      {/* Toast Notifications */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      <div style={gridStyle}>
        <StatCard
          title="Total Team Leads"
          value={stats.total}
          icon=""
          color="#3b82f6"
        />
        <StatCard
          title="Active Today"
          value={stats.active}
          icon=""
          subtitle="Currently working"
          color="#10b981"
        />
        {/* <StatCard
          title="Available"
          value={stats.available}
          icon=""
          subtitle="Ready for assignment"
          color="#06b6d4"
        /> */}
      </div>

      <div style={filterBarStyle}>
        <div style={filterGroupStyle}>
          <Input
            placeholder="Search team leads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
            }
          />
        </div>
        <div style={filterGroupStyle}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px' }}>
            Filter by Status
          </label>
          <select
            style={selectStyle}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="ALL">All Status</option>
            <option value="Active">Active</option>
            <option value="Available">Available</option>
            <option value="Busy">Busy</option>
            <option value="On Leave">On Leave</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
          <Button variant="secondary" onClick={loadTeamLeads}>
             Refresh
          </Button>
          <Button variant="primary" onClick={() => setShowAddModal(true)}>
             Add Team Lead
          </Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredTeamLeads}
        actions={[
          // { label: ' Performance', onClick: handleViewPerformance },
          { label: ' Edit', onClick: handleEditTeamLead },
          { label: ' Delete', onClick: handleDeleteTeamLead }
        ]}
        pageSize={15}
      />

      {/* Modals */}
      <AddTeamLeadModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddTeamLead}
      />

      <EditTeamLeadModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedTeamLead(null);
        }}
        onSubmit={handleUpdateTeamLead}
        teamLead={selectedTeamLead}
      />

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSelectedTeamLead(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Team Lead"
        message={deleteMessage}
        confirmText="Delete"
        type="danger"
      />
    </div>
  );
};

export default TeamLeadsView;